import * as Markdownit from 'markdown-it';
import * as React from 'react';

import { CheckBoxWithLabelWidget } from '@tableau/widgets';
import { Messages } from '../../../codegen/Localize';
import { sendGetServerWelcomeChannelInfoRequest } from '../../../home/sagas/HomeSaga';
import { GetServerWelcomeChannelInfoResult } from '../../../ts/ServerApi/requests/GetServerWelcomeChannelInfoRequest';
import { CustomizationSettingsCtrlProps } from '../controllers/CustomizationSettingsCtrl';
import { SettingsWelcomeChannelStyles } from '../styles/SettingsWelcomeChannelStyles';
import { htmlToMarkdown } from '../utils/MarkdownHtml';
import { FooterEditor } from './FooterEditor';
import { WelcomeChannelNavigationDialog } from './WelcomeChannelNavigationDialog';

export type FormControlElement<T> = {
  value: T;
  initialValue: T;
  dirty: boolean; // Whether the value is different from initial value
  valid: boolean;
};

type ControlValue = boolean | string;

type FormControl<Controls> = { [key in keyof Controls]: FormControlElement<ControlValue> };

export enum ServerFormControls {
  defaultBannerCheckbox = 'defaultBannerCheckbox',
  serverFooterCheckbox = 'serverFooterCheckbox',
  serverFooter = 'serverFooter',
}

interface CustomizationSettingsState {
  formControls: FormControl<typeof ServerFormControls>;
  isBusy: boolean;
  showNavigationDialog: boolean;
  navigationCallBack: () => void;
}

export class CustomizationSettings extends React.PureComponent<CustomizationSettingsCtrlProps, CustomizationSettingsState> {

  private markdownConverter: Markdownit;

  constructor(props: CustomizationSettingsCtrlProps) {
    super(props);
    props.setSave(this.handleSave);
    props.setIsBusy(this.handleBusy);
    props.setCancel(this.resetForm);
    props.setOpenNavigationDialog(this.handleShowNavigationDialog);

    this.markdownConverter = new Markdownit();
    this.state = {
      formControls: {
        [ServerFormControls.defaultBannerCheckbox]: {
          value: false,
          initialValue: false,
          dirty: false,
          valid: true
        },
        [ServerFormControls.serverFooterCheckbox]: {
          value: false,
          initialValue: false,
          dirty: false,
          valid: true
        },
        [ServerFormControls.serverFooter]: {
          value: '',
          initialValue: '',
          dirty: false,
          valid: true
        }
      },
      isBusy: false,
      navigationCallBack: null,
      showNavigationDialog: false,
    };
  }

  public componentDidMount() {
    const getServerWelcomeChannelRequest = sendGetServerWelcomeChannelInfoRequest();
    getServerWelcomeChannelRequest.then(result => {
      this.setupFormUsingGetChannelInfo(result);
    });
  }

  render() {
    return(
      <section>
        <div className="tb-settings-group-name">{Messages.settings_welcomeChannel_title()}</div>
        <div className="tb-indent">
          <p style={SettingsWelcomeChannelStyles.settingsDescription}>{Messages.settings_welcomeChannel_description()}</p>
          <CheckBoxWithLabelWidget
            checked={this.state.formControls.defaultBannerCheckbox.value as boolean}
            handleChange={(isBoxChecked: boolean) => {
              this.handleOnChange(ServerFormControls.defaultBannerCheckbox, isBoxChecked);
            }}
            testId="defaultBanner"
            label={Messages.settings_welcomeChannel_defaultBannerCheckboxLabel()} />
          <CheckBoxWithLabelWidget
            checked={this.state.formControls.serverFooterCheckbox.value as boolean}
            handleChange={(isBoxChecked: boolean) => {
              this.handleOnChange(ServerFormControls.serverFooterCheckbox, isBoxChecked);
            }}
            testId="customServerFooter"
            label={Messages.settings_welcomeChannel_customServerFooterCheckboxLabel()} />
          {this.state.formControls[ServerFormControls.serverFooterCheckbox].value && <FooterEditor
            formName={ServerFormControls.serverFooter}
            value={this.state.formControls.serverFooter.value as string}
            updateForm={this.handleOnChange}
            showDefaultWelcomeChannel={this.state.formControls.defaultBannerCheckbox.value as boolean}
          />}
          {this.state.showNavigationDialog && <WelcomeChannelNavigationDialog
            handleClose={this.handleHideNavigationDialog}
            handleNavigation={this.state.navigationCallBack}
            handleSave={this.handleSave}
          />}
        </div>
      </section>
    );
  }

  private handleBusy = () => {
    return this.state.isBusy;
  }

  private handleSave = () => {
    this.setState({ isBusy: true }, () => {
      let footerAsMarkdown = '';
      if (this.state.formControls.serverFooterCheckbox.value) { // Only set the footer if the checkbox is been enabled
        footerAsMarkdown = htmlToMarkdown(this.state.formControls.serverFooter.value as string);
      }

      this.props.sendUpdateRequest(
        this.state.formControls.defaultBannerCheckbox.value as boolean,
        footerAsMarkdown
      ).finally(() => {
        this.setState({ isBusy: false });
        this.props.setIsFormDirty(false);
        this.updateFormOnSave();
      });
    });
  }

  private handleOnChange = (formName: ServerFormControls, value: ControlValue, valid: boolean = true) => {
    const cleanedValue = this.cleanValue(value);
    const cleanedInitialValue = this.cleanValue(this.state.formControls[formName].initialValue);

    const updatedFormControls = {
      ...this.state.formControls
    };
    const updatedFormElement = {
      ...updatedFormControls[formName],
      value: cleanedValue,
      dirty: cleanedValue !== cleanedInitialValue,
      valid
    };

    updatedFormControls[formName] = updatedFormElement;

    this.updateFormControls(updatedFormControls);
  }

  private handleShowNavigationDialog = (successCallback: () => void) => {
    this.setState({
      showNavigationDialog: true,
      navigationCallBack: successCallback
    });
  }

  private handleHideNavigationDialog = () => {
    this.setState({
      showNavigationDialog: false
    });
  }

  private setupFormUsingGetChannelInfo(result: GetServerWelcomeChannelInfoResult) {
    const enabledForServer: boolean = result.welcomeChannelEnabledForServer;
    const serverFooter: string = result.welcomeChannelServerFooter;

    const updatedFormControls = {
      ...this.state.formControls
    };
    const updatedDefaultBannerCheckbox = {
      ...updatedFormControls[ServerFormControls.defaultBannerCheckbox],
      value: enabledForServer,
      initialValue: enabledForServer
    };
    // Because we don't have persistant data for this checkbox, use the server footer to determine whether it's checked or not
    const updatedServerFooterCheckbox = {
      ...updatedFormControls[ServerFormControls.serverFooterCheckbox],
      value: serverFooter ? true : false,
      initialValue: serverFooter ? true : false
    };
    const updatedServerFooter = {
      ...updatedFormControls[ServerFormControls.serverFooter],
      value: this.convertMarkdownToHtml(serverFooter),
      initialValue: this.convertMarkdownToHtml(serverFooter)
    };

    updatedFormControls[ServerFormControls.defaultBannerCheckbox] = updatedDefaultBannerCheckbox;
    updatedFormControls[ServerFormControls.serverFooterCheckbox] = updatedServerFooterCheckbox;
    updatedFormControls[ServerFormControls.serverFooter] = updatedServerFooter;

    this.setState(() => ({
      formControls: updatedFormControls
    }));
  }

  private resetForm = () => {
    this.setFormUsing('initialValue');
  }

  private updateFormOnSave = () => {
    this.setFormUsing('value');
  }

  private updateFormControls = (newFormControls: FormControl<typeof ServerFormControls>) => {
    this.setState(() => ({
      formControls: newFormControls
    }), () => {
        this.props.setIsFormValid(this.isFormValid());
        this.props.setIsFormDirty(this.isFormDirty());
    });
  }

  private setFormUsing(newValueAttribute: keyof FormControlElement<ControlValue>) {
    const updatedFormControls = {
      ...this.state.formControls
    };
    Object.keys(updatedFormControls).forEach((controlName: ServerFormControls) => {
      const control = updatedFormControls[controlName];
      updatedFormControls[controlName] = {
        value: control[newValueAttribute],
        initialValue: control[newValueAttribute],
        dirty: false,
        valid: true
      };
    });
    this.setState({
      formControls: updatedFormControls
    });
    this.props.setIsFormDirty(false);
  }

  private convertMarkdownToHtml(markdown: string): string {
    return this.markdownConverter.render(markdown);
  }

  private isFormDirty(): boolean {
    return (Object.keys(this.state.formControls) as ServerFormControls[])
        .some(controlName => this.state.formControls[controlName].dirty);
  }
  private isFormValid(): boolean {
    return (Object.keys(this.state.formControls) as ServerFormControls[])
      .every(controlName => this.state.formControls[controlName].valid);
  }

  private cleanValue(value: ControlValue): ControlValue {
    return typeof value === 'string' ? value.trim() : value;
  }
}
